import { drag, select } from "d3";
import { RefObject, useEffect, useState } from "react";

import { usePlayContext } from "../../../pages";
import { DragGroupElement, FrameStencil, Point } from "../../../types";
import { usePlayingSurfaceContext } from "../../playing-surface";

interface UseRotateStencil {
  stencilWrapperRef: RefObject<SVGGElement>;
  rotationDraggerRef: RefObject<SVGCircleElement>;
  stencil: FrameStencil;
  snapToAngle?: boolean;
}

export const useRotateStencil = ({
  stencilWrapperRef,
  rotationDraggerRef,
  stencil,
  snapToAngle = false,
}: UseRotateStencil) => {
  const { rotate } = stencil;

  const { frameStencilsApi } = usePlayContext();
  const { updateStencilRotation } = frameStencilsApi;

  const { isEditable } = usePlayingSurfaceContext();

  const [tempRotation, setTempRotation] = useState<number>(rotate || 0);
  const [rotationCenter, setRotationCenter] = useState<Point>({ x: 0, y: 0 });

  useEffect(() => {
    const stencilWrapperElement = stencilWrapperRef.current;
    const circle = rotationDraggerRef.current;

    if (circle && isEditable && stencilWrapperElement) {
      const bb = stencilWrapperElement.getBoundingClientRect();
      const { left, top, width, height } = bb;

      setRotationCenter({ x: left + width / 2, y: top + height / 2 });

      let center: Point;

      select<SVGCircleElement, undefined>(circle).call(
        drag<SVGCircleElement, undefined>()
          .on("start", function () {
            const bb = stencilWrapperElement.getBoundingClientRect();
            const { left, top, width, height } = bb;

            center = { x: left + width / 2, y: top + height / 2 };
          })
          .on("drag", function (event: DragGroupElement<SVGCircleElement>) {
            const sourceEvent = event.sourceEvent as MouseEvent;
            const { clientX, clientY } = sourceEvent;
            const dx = clientX - center.x;
            const dy = clientY - center.y;
            let angle = (180 / Math.PI) * Math.atan2(dy, dx);

            if (snapToAngle) {
              // Snap to 0, 90, 180, or 270 degrees if within 20 degrees
              const snapAngles = [0, 90, 180, -90];
              const tolerance = 20;
              for (const snapAngle of snapAngles) {
                if (Math.abs(angle - snapAngle) <= tolerance) {
                  angle = snapAngle;
                  break;
                }
              }
            }

            setTempRotation(angle + 90);
          })
          .on("end", function (event: DragGroupElement<SVGCircleElement>) {
            const sourceEvent = event.sourceEvent as MouseEvent;
            const { clientX, clientY } = sourceEvent;
            const dx = clientX - center.x;
            const dy = clientY - center.y;
            let angle = (180 / Math.PI) * Math.atan2(dy, dx);

            if (snapToAngle) {
              // Snap to 0, 90, 180, or 270 degrees if within 20 degrees
              const snapAngles = [0, 90, 180, -90];
              const tolerance = 20;
              for (const snapAngle of snapAngles) {
                if (Math.abs(angle - snapAngle) <= tolerance) {
                  angle = snapAngle;
                  break;
                }
              }
            }

            updateStencilRotation({ stencil, rotation: angle + 90 });
          })
      );
    } else if (circle && !isEditable) {
      select<SVGCircleElement, undefined>(circle).on(".drag", null);
    }
  }, [isEditable, stencilWrapperRef, rotationDraggerRef, stencil, updateStencilRotation, snapToAngle]);

  return { tempRotation, rotationCenter };
};
